// store/index.ts
import { configureStore } from "@reduxjs/toolkit";
import userReducer from "./slices/userSlice";
import chatReducer from "./slices/chatSlice";
import feedbackReducer from "./slices/feedbackSlice";

export const store = configureStore({
    reducer: {
        user: userReducer,
        chat: chatReducer,
        feedback: feedbackReducer
    }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Re-export everything for clean imports
export { useAppDispatch, useAppSelector } from "./hooks";

// Slices
export {
    initializeUser,
    logout,
    clearError as clearUserError,
} from "./slices/userSlice";

export {
    fetchChatHistories,
    fetchMessages,
    createChatHistory,
    sendMessage,
    submitFeedback,
    clearChatHistory,
    deleteChatHistory,
    setActiveChatHistory,
    addOptimisticMessage,
    updateMessageStatus,
    appendToMessage,
    setStreaming,
    updateChatHistoryTitle,
    clearError as clearChatError,
    // Chat page exports
    sendChatMessage,
    submitChatFeedback,
    submitChatAdditionalFeedback,
    setAnswers,
    addAnswer,
    setStreamedAnswers,
    setLastQuestion,
    setLoading,
    setError,
    setConversationLogId,
    clearChatState
} from "./slices/chatSlice";

export { submitFeedbackForm, setFeedbackMessage, resetFeedback, clearFeedbackError } from "./slices/feedbackSlice";

// Selectors
export {
    selectUser,
    selectUserId,
    selectAuthEnabled,
    selectGuestAccessEnabled,
    selectAppFeedbackEnabled,
    selectChatFeedbackEnabled,
    selectStreamingEnabled,
    selectIsGuest,
    selectUserRoles,
    selectIsAuthenticated,
    selectUserLoading,
    selectUserError,
    selectUserDisplayName,
    selectDomainRouting,
    selectDomainConfig,
} from "./selectors/userSelectors";

export {
    selectChatHistories,
    selectMessages,
    selectMessagesByChatHistory,
    selectActiveChatHistoryId,
    selectChatLoading,
    selectIsStreaming,
    selectChatError,
    selectChatHistoriesList,
    selectActiveChatHistory,
    selectActiveChatHistoryMessages,
    selectMessagesByChatHistoryId,
    selectMessagePairs,
    selectFeedbackStats,
    selectMessageById,
    selectChatHistoryHasMessages,
    // Chat page selectors
    selectAnswers,
    selectStreamedAnswers,
    selectLastQuestion,
    selectConversationLogId,
    selectHasChatStarted
} from "./selectors/chatSelectors";

export { selectFeedbackMessage, selectFeedbackIsSubmitting, selectFeedbackIsSubmitted, selectFeedbackError } from "./selectors/feedbackSelectors";

// Types
export type { User, UserState, AuthType, UserRole } from "./types/userTypes";
export { hasRole, hasMinRole, getHighestRole } from "./types/userTypes";
export type {
    Message,
    ChatHistoryEntry,
    ChatState,
    MessageRole,
    MessageStatus,
    Feedback,
    FeedbackType,
    SendMessagePayload,
    SubmitFeedbackPayload,
    ChatAnswerTuple
} from "./types/chatTypes";
export type { FeedbackSubmission, FeedbackState } from "./types/feedbackTypes";
